import React from "react";
import {
  Stack,
  Grid,
  Switch,
  Select,
  TextInput,
  NumberInput,
  JsonInput,
  Fieldset,
  Text,
  Alert,
  ColorInput,
  ScrollArea,
} from "@mantine/core";
import { IconEye } from "@tabler/icons-react";
import DemoSettings from "../../components/demo-settings";

export const BasicTab = ({ globalState }) => (
  <ScrollArea h="calc(100vh - 400px)">
    <DemoSettings />
  </ScrollArea>
);

export const JourneyTab = ({ globalState, setContextValue, updateAdvancedSettings }) => (
  <ScrollArea h="calc(100vh - 400px)">
    <Stack gap="lg">
      <Grid>
        <Grid.Col span={6}>
          <Switch
            label="Test Mode"
            checked={globalState.advancedSettings?.settings?.test_mode || false}
            onChange={(e) => updateAdvancedSettings('settings.test_mode', e.currentTarget.checked)}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Switch
            label="Block Verification"
            checked={globalState.advancedSettings?.settings?.block || false}
            onChange={(e) => updateAdvancedSettings('settings.block', e.currentTarget.checked)}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Switch
            label="Show Close Button"
            checked={globalState.advancedSettings?.settings?.isCloseButtonShown || false}
            onChange={(e) => updateAdvancedSettings('settings.isCloseButtonShown', e.currentTarget.checked)}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Switch
            label="Continue on Desktop"
            checked={globalState.advancedSettings?.settings?.shouldShowContinueOnDesktop || false}
            onChange={(e) => updateAdvancedSettings('settings.shouldShowContinueOnDesktop', e.currentTarget.checked)}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Switch
            label="React Native Mode"
            checked={globalState.advancedSettings?.settings?.isReactNative || false}
            onChange={(e) => updateAdvancedSettings('settings.isReactNative', e.currentTarget.checked)}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Switch
            label="Show Footer"
            checked={globalState.advancedSettings?.settings?.isFooterShown || false}
            onChange={(e) => updateAdvancedSettings('settings.isFooterShown', e.currentTarget.checked)}
          />
        </Grid.Col>
      </Grid>
      
      <Fieldset legend="Webhook Configuration">
        <Stack gap="md">
          <TextInput
            label="Webhook Endpoint"
            value={globalState.advancedSettings?.webhook?.endpoint || ""}
            onChange={(e) => updateAdvancedSettings('webhook.endpoint', e.target.value)}
            placeholder="https://your-webhook-url.com"
          />
          
          <Grid>
            <Grid.Col span={6}>
              <Switch
                label="Enable HMAC"
                checked={globalState.advancedSettings?.webhook?.hmac?.enabled || false}
                onChange={(e) => updateAdvancedSettings('webhook.hmac.enabled', e.currentTarget.checked)}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="HMAC Secret"
                value={globalState.advancedSettings?.webhook?.hmac?.secret || ""}
                onChange={(e) => updateAdvancedSettings('webhook.hmac.secret', e.target.value)}
                placeholder="your-hmac-secret-key"
                disabled={!globalState.advancedSettings?.webhook?.hmac?.enabled}
              />
            </Grid.Col>
          </Grid>
          
          <Grid>
            <Grid.Col span={6}>
              <Switch
                label="Enable Basic Auth"
                checked={globalState.advancedSettings?.webhook?.basicAuth?.enabled || false}
                onChange={(e) => updateAdvancedSettings('webhook.basicAuth.enabled', e.currentTarget.checked)}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Basic Auth Username"
                value={globalState.advancedSettings?.webhook?.basicAuth?.username || ""}
                onChange={(e) => updateAdvancedSettings('webhook.basicAuth.username', e.target.value)}
                placeholder="username"
                disabled={!globalState.advancedSettings?.webhook?.basicAuth?.enabled}
              />
            </Grid.Col>
            <Grid.Col span={6} offset={6}>
              <TextInput
                label="Basic Auth Password"
                type="password"
                value={globalState.advancedSettings?.webhook?.basicAuth?.password || ""}
                onChange={(e) => updateAdvancedSettings('webhook.basicAuth.password', e.target.value)}
                placeholder="password"
                disabled={!globalState.advancedSettings?.webhook?.basicAuth?.enabled}
              />
            </Grid.Col>
          </Grid>
        </Stack>
      </Fieldset>
    </Stack>
  </ScrollArea>
);

export const IntegrationTab = ({ globalState, setContextValue, updateAddons }) => (
  <ScrollArea h="calc(100vh - 400px)">
    <Stack gap="lg">
      <Grid>
        <Grid.Col span={6}>
          <Select
            label="Integration Method"
            value={globalState.method}
            onChange={(value) => setContextValue('method', value)}
            data={[
              { label: "Popup", value: "popup" },
              { label: "Redirect", value: "redirect" },
              { label: "Embedded", value: "embedded" }
            ]}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Select
            label="Language"
            value={globalState.language}
            onChange={(value) => setContextValue('language', value)}
            data={[
              { label: "English", value: "en" },
              { label: "Spanish", value: "es" },
              { label: "French", value: "fr" },
              { label: "German", value: "de" },
              { label: "Italian", value: "it" },
              { label: "Portuguese", value: "pt" },
              { label: "Chinese", value: "zh" },
              { label: "Japanese", value: "ja" },
              { label: "Korean", value: "ko" }
            ]}
          />
        </Grid.Col>
      </Grid>

      <Fieldset legend="Add-ons & Features">
        <Grid>
          {Object.entries(globalState.addons).map(([key, addon]) => (
            <Grid.Col key={key} span={6}>
              <Switch
                label={addon.label}
                description={addon.description}
                checked={addon.checked}
                onChange={(e) => updateAddons(key, { ...addon, checked: e.currentTarget.checked })}
              />
            </Grid.Col>
          ))}
        </Grid>
      </Fieldset>
    </Stack>
  </ScrollArea>
);

export const StylingTab = ({ globalState, updateAdvancedSettings }) => (
  <ScrollArea h="calc(100vh - 400px)">
    <Stack gap="lg">
      <Fieldset legend="Basic Styles">
        <Grid>
          <Grid.Col span={12}>
            <TextInput
              label="Logo URL"
              value={globalState.advancedSettings?.styles?.logo_url || ""}
              onChange={(e) => updateAdvancedSettings('styles.logo_url', e.target.value)}
              placeholder="https://example.com/logo.png"
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Select
              label="Color Scheme"
              value={globalState.advancedSettings?.styles?.colorScheme || "light"}
              onChange={(value) => updateAdvancedSettings('styles.colorScheme', value)}
              data={[
                { label: "Light", value: "light" },
                { label: "Dark", value: "dark" }
              ]}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Select
              label="Default Radius"
              value={globalState.advancedSettings?.styles?.defaultRadius || "md"}
              onChange={(value) => updateAdvancedSettings('styles.defaultRadius', value)}
              data={[
                { label: "Extra Small", value: "xs" },
                { label: "Small", value: "sm" },
                { label: "Medium", value: "md" },
                { label: "Large", value: "lg" },
                { label: "Extra Large", value: "xl" }
              ]}
            />
          </Grid.Col>
        </Grid>
      </Fieldset>

      <Fieldset legend="Button Styles">
        <Grid>
          <Grid.Col span={6}>
            <Select
              label="Button Radius"
              value={globalState.advancedSettings?.styles?.button?.radius || "md"}
              onChange={(value) => updateAdvancedSettings('styles.button.radius', value)}
              data={[
                { label: "Extra Small", value: "xs" },
                { label: "Small", value: "sm" },
                { label: "Medium", value: "md" },
                { label: "Large", value: "lg" },
                { label: "Extra Large", value: "xl" }
              ]}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <ColorInput
              label="Button Color"
              value={globalState.advancedSettings?.styles?.components?.button?.color || "#000000"}
              onChange={(value) => updateAdvancedSettings('styles.components.button.color', value)}
            />
          </Grid.Col>
        </Grid>
      </Fieldset>

      <Fieldset legend="Typography">
        <Grid>
          <Grid.Col span={6}>
            <TextInput
              label="Font Family"
              value={globalState.advancedSettings?.styles?.global?.typography?.font?.family || ""}
              onChange={(e) => updateAdvancedSettings('styles.global.typography.font.family', e.target.value)}
              placeholder="SpaceGrotesk"
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Font URL"
              value={globalState.advancedSettings?.styles?.global?.typography?.font?.normal?.url || ""}
              onChange={(e) => updateAdvancedSettings('styles.global.typography.font.normal.url', e.target.value)}
              placeholder="https://example.com/fonts/font.ttf"
            />
          </Grid.Col>
        </Grid>
      </Fieldset>

      <Fieldset legend="Custom Color Palette">
        <JsonInput
          label="Custom Colors (JSON)"
          placeholder='{"custom-green": ["#B3ECE7", "#8FE3DB", ...]}'
          value={JSON.stringify(globalState.advancedSettings?.styles?.colors || {}, null, 2)}
          onChange={(value) => {
            try {
              const parsed = JSON.parse(value);
              updateAdvancedSettings('styles.colors', parsed);
            } catch (e) {
              // Invalid JSON
            }
          }}
          minRows={4}
          maxRows={8}
          formatOnBlur
        />
      </Fieldset>
    </Stack>
  </ScrollArea>
);

export const DataTab = ({ globalState, updateAdvancedSettings }) => (
  <ScrollArea h="calc(100vh - 400px)">
    <Stack gap="md">
      <Alert icon={<IconEye size={16} />} color="blue" variant="light">
        <Text size="sm" fw={500} mb="xs">Demo Purpose Only</Text>
        <Text size="xs">
          Select which data fields you want to demonstrate passing to the verification journey. 
          In production, clients can pass any customer data at the start of a journey to pre-populate forms.
        </Text>
      </Alert>
      
      <Text size="sm" fw={500}>Select fields to include in the demo:</Text>
      
      <Grid>
        <Grid.Col span={6}>
          <Switch
            label="First Name (fn)"
            checked={globalState.advancedSettings?.dataFields?.fn || false}
            onChange={(e) => updateAdvancedSettings('dataFields.fn', e.currentTarget.checked)}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Switch
            label="Last Name (ln)"
            checked={globalState.advancedSettings?.dataFields?.ln || false}
            onChange={(e) => updateAdvancedSettings('dataFields.ln', e.currentTarget.checked)}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Switch
            label="Address (addr)"
            checked={globalState.advancedSettings?.dataFields?.addr || false}
            onChange={(e) => updateAdvancedSettings('dataFields.addr', e.currentTarget.checked)}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Switch
            label="City"
            checked={globalState.advancedSettings?.dataFields?.city || false}
            onChange={(e) => updateAdvancedSettings('dataFields.city', e.currentTarget.checked)}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Switch
            label="State"
            checked={globalState.advancedSettings?.dataFields?.state || false}
            onChange={(e) => updateAdvancedSettings('dataFields.state', e.currentTarget.checked)}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Switch
            label="ZIP Code"
            checked={globalState.advancedSettings?.dataFields?.zip || false}
            onChange={(e) => updateAdvancedSettings('dataFields.zip', e.currentTarget.checked)}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Switch
            label="Phone Number"
            checked={globalState.advancedSettings?.dataFields?.phone || false}
            onChange={(e) => updateAdvancedSettings('dataFields.phone', e.currentTarget.checked)}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Switch
            label="Email"
            checked={globalState.advancedSettings?.dataFields?.email || false}
            onChange={(e) => updateAdvancedSettings('dataFields.email', e.currentTarget.checked)}
          />
        </Grid.Col>
      </Grid>

      <Fieldset legend="Additional Data (JSON)">
        <JsonInput
          label="Custom Additional Data"
          placeholder="Enter custom data as JSON"
          value={JSON.stringify(globalState.advancedSettings?.additionalData || {}, null, 2)}
          onChange={(value) => {
            try {
              const parsed = JSON.parse(value);
              updateAdvancedSettings('additionalData', parsed);
            } catch (e) {
              // Invalid JSON
            }
          }}
          minRows={4}
          maxRows={8}
          formatOnBlur
        />
      </Fieldset>
    </Stack>
  </ScrollArea>
);

export const MessagesTab = ({ globalState, updateAdvancedSettings }) => (
  <ScrollArea h="calc(100vh - 400px)">
    <Stack gap="md">
      <Fieldset legend="Success Messages">
        <Grid>
          <Grid.Col span={12}>
            <TextInput
              label="Success Title"
              value={globalState.advancedSettings?.resultMessages?.success?.verificationSuccessTitle || ""}
              onChange={(e) => updateAdvancedSettings('resultMessages.success.verificationSuccessTitle', e.target.value)}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <TextInput
              label="Success Subtitle"
              value={globalState.advancedSettings?.resultMessages?.success?.verificationSuccessSubTitle || ""}
              onChange={(e) => updateAdvancedSettings('resultMessages.success.verificationSuccessSubTitle', e.target.value)}
            />
          </Grid.Col>
        </Grid>
      </Fieldset>
      
      <Fieldset legend="Failure Messages">
        <Grid>
          <Grid.Col span={12}>
            <TextInput
              label="Failure Title"
              value={globalState.advancedSettings?.resultMessages?.failure?.verificationFailureTitle || ""}
              onChange={(e) => updateAdvancedSettings('resultMessages.failure.verificationFailureTitle', e.target.value)}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <TextInput
              label="Failure Subtitle"
              value={globalState.advancedSettings?.resultMessages?.failure?.verificationFailureSubTitle || ""}
              onChange={(e) => updateAdvancedSettings('resultMessages.failure.verificationFailureSubTitle', e.target.value)}
            />
          </Grid.Col>
        </Grid>
      </Fieldset>
      
      <Fieldset legend="Error Messages">
        <Grid>
          <Grid.Col span={12}>
            <TextInput
              label="Error Title"
              value={globalState.advancedSettings?.resultMessages?.error?.verificationErrorTitle || ""}
              onChange={(e) => updateAdvancedSettings('resultMessages.error.verificationErrorTitle', e.target.value)}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <TextInput
              label="Error Subtitle"
              value={globalState.advancedSettings?.resultMessages?.error?.verificationErrorSubTitle || ""}
              onChange={(e) => updateAdvancedSettings('resultMessages.error.verificationErrorSubTitle', e.target.value)}
            />
          </Grid.Col>
        </Grid>
      </Fieldset>
    </Stack>
  </ScrollArea>
);